import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import RequireCompany from "@/components/RequireCompany";
import PageHeader from "@/components/PageHeader";
import ExportToolbar from "@/components/ExportToolbar";
import DateRangePicker from "@/components/DateRangePicker";
import { useReportRange, formatRangeLabel } from "@/lib/useReportRange";
import { exportExcel, exportPDF } from "@/lib/exporters";
import { devError } from "@/lib/logger";

function OutstandingTable({ title, rows }) {
  const total = rows.reduce((s, r) => s + r.amount, 0);
  return (
    <div className="border border-gray-200 rounded bg-white overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wider text-gray-600 font-semibold">{title}</div>
      <table className="tx-table">
        <tbody>
          {rows.length === 0 && <tr><td colSpan={2} className="text-center py-6 text-gray-400">No outstanding</td></tr>}
          {rows.map((r) => <tr key={r.name}><td>{r.name}</td><td className="tx-num">{formatINR(r.amount)}</td></tr>)}
          {rows.length > 0 && (
            <tr className="bg-gray-50 font-semibold border-t-2 border-gray-300">
              <td>Total</td><td className="tx-num">{formatINR(total)}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Inner() {
  const { activeId, active } = useCompany();
  const { range, setRange, applied, apply, params } = useReportRange();
  const dirty = range.start !== applied.start || range.end !== applied.end;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    let mounted = true;
    api.get(`/companies/${activeId}/reports/outstanding`, { params })
      .then((r) => { if (mounted) setData(r.data); })
      .catch((err) => devError("Outstanding report failed", err));
    return () => { mounted = false; };
  }, [activeId, applied.start, applied.end, params]);

  const onExcel = () => {
    if (!data) return;
    exportExcel(`outstanding-${active?.name || "report"}`, [
      { name: "Receivables", rows: [[`As on: ${applied.end || "—"}`], [], ["Party", "Amount"], ...data.receivables.map((r) => [r.name, r.amount])] },
      { name: "Payables", rows: [["Party", "Amount"], ...data.payables.map((r) => [r.name, r.amount])] },
    ]);
  };
  const onPDF = () => {
    if (!data) return;
    exportPDF(`outstanding-${active?.name || "report"}`, "Outstanding Receivables & Payables", [
      { title: `As on: ${applied.end || "—"} — Receivables (Sundry Debtors)`, headers: ["Party", "Amount (₹)"], rows: data.receivables.map((r) => [r.name, formatINR(r.amount)]) },
      { title: "Payables (Sundry Creditors)", headers: ["Party", "Amount (₹)"], rows: data.payables.map((r) => [r.name, formatINR(r.amount)]) },
    ], { company: active?.name, date: applied.end });
  };

  return (
    <>
      <PageHeader title="Outstanding" subtitle={`${active?.name || ""}  •  ${formatRangeLabel(range)}`}
        actions={<ExportToolbar onExcel={onExcel} onPDF={onPDF} onPrint={() => window.print()} />}
      />
      <div className="p-6 space-y-4" data-testid="outstanding-report">
        <DateRangePicker start={range.start} end={range.end} onChange={setRange} onApply={apply} dirty={dirty} label="As on" />
        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <OutstandingTable title="Receivables (Sundry Debtors)" rows={data.receivables} />
            <OutstandingTable title="Payables (Sundry Creditors)" rows={data.payables} />
          </div>
        )}
      </div>
    </>
  );
}

export default function Outstanding() { return <RequireCompany><Inner /></RequireCompany>; }
